<template>
  <div class="auth-container">
    <div class="auth-card">
      <div>
        <div class="logo-container">
          <img src="@/assets/query-img.svg" :alt="$t('auth.common.logoAlt')" />
        </div>
        <a @click.prevent="goBack" class="back-link">{{ $t('auth.common.back') }}</a>

        <!-- Étape 1 : email + mot de passe -->
        <div v-if="step === 1">
          <h5 class="form-title">{{ $t('auth.login.title') }}</h5>
          <form @submit.prevent="requestOtp" class="auth-form">
            <div class="form-field">
              <label class="input-label" for="email"></label>
              <input
                type="email"
                class="form-input"
                id="email"
                v-model="email"
                required
                autocomplete="email"
                :class="{ 'error-input': loginError || emailNotVerified }"
                :placeholder="$t('auth.common.yourEmail')"
              />
            </div>

            <div class="form-field">
              <label class="input-label" for="password"></label>
              <div class="password-container">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input"
                  id="password"
                  v-model="password"
                  required
                  autocomplete="current-password"
                  :class="{ 'error-input': loginError || emailNotVerified }"
                  :placeholder="$t('auth.common.yourPassword')"
                />
                <img
                  class="eye-icon"
                  src="@/assets/eye-icon.svg"
                  :alt="$t('auth.common.togglePasswordAlt')"
                  @click="togglePassword"
                />
              </div>
              <div v-if="loginError" class="error-message">
                {{ $t('auth.login.passwordIncorrect') }}
              </div>
              <div v-if="emailNotVerified" class="error-message">
                {{ $t('auth.login.emailNotVerified') }}
                <a href="#" @click.prevent="handleResendVerification" class="resend-link">{{ $t('auth.login.resendVerification') }}</a>
              </div>
            </div>

            <div class="form-options">
              <router-link class="forgot-password" to="/forgot-password">{{ $t('auth.login.forgotPassword') }}</router-link>
            </div>

            <button type="submit" class="auth-button primary-button" :disabled="isLoading">
              <span v-if="isLoading">{{ $t('auth.login.submitting') }}</span>
              <span v-else>{{ $t('auth.login.loginButton') }}</span>
            </button>

            <div class="divider"></div>

            <button
              type="button"
              @click="handleMicrosoftLogin"
              class="auth-button oauth-button microsoft-button"
            >
              <svg class="oauth-logo" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
              <span>{{ $t('auth.login.continueMicrosoft') }}</span>
            </button>

            <p class="signup-text">
              {{ $t('auth.login.noAccount') }}
              <a
                href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
                @click.prevent="openCalendlyDemo"
              >{{ $t('auth.login.bookDemo') }}</a>
            </p>
          </form>
        </div>

        <!-- Étape 2 : saisie du code OTP -->
        <div v-else-if="step === 2">
          <h5 class="form-title">{{ $t('auth.login.otpTitle') }}</h5>
          <p class="form-subtitle">
            <i18n path="auth.login.otpSentTo" tag="span">
              <template #email><strong>{{ maskedEmail }}</strong></template>
            </i18n>
          </p>

          <form @submit.prevent="verifyOtp" class="auth-form">
            <div class="form-field">
              <input
                type="text"
                class="form-input"
                v-model="otpCode"
                required
                :placeholder="$t('auth.login.otpPlaceholder')"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
                :class="{ 'error-input': otpError }"
              />
              <div v-if="otpError" class="error-message">{{ otpError }}</div>
            </div>

            <button type="submit" class="auth-button primary-button" :disabled="isVerifying">
              <span v-if="isVerifying">{{ $t('auth.login.verifying') }}</span>
              <span v-else>{{ $t('auth.login.validate') }}</span>
            </button>

            <button
              type="button"
              @click="resendOtp"
              class="auth-button secondary-button"
              :disabled="resendCooldown > 0 || isLoading"
            >
              <span v-if="resendCooldown > 0">{{ $t('auth.login.resendIn', { seconds: resendCooldown }) }}</span>
              <span v-else>{{ $t('auth.login.resendCode') }}</span>
            </button>

            <button
              type="button"
              @click="backToStep1"
              class="auth-button back-button"
            >
              {{ $t('auth.login.changeEmail') }}
            </button>
          </form>
        </div>
      </div>
      <div class="copyright">{{ $t('auth.common.copyright') }}</div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

.back-link {
  display: block;
  text-align: left;
  margin-top: 40px;
  margin-bottom: 15px;
  color: #6c63ff;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
}

.back-link:hover {
  text-decoration: underline;
}

.auth-container {
  display: flex;
  justify-content: center;
  background: url("@/assets/sign-In-bg.png");
  background-size: cover;
  background-position: center;
  height: 100vh;
  font-family: 'Roboto', sans-serif;
}

.auth-card {
  width: 500px;
  padding: 0 80px;
  background-color: white;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.logo-container {
  width: 100%;
  height: 130px;
  background: black;
  border-radius: 0 0 20px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.form-title {
  text-align: left;
  font-weight: bold;
  margin-top: 0;
  font-size: 18px;
}

.form-subtitle {
  text-align: left;
  font-size: 14px;
  color: #666;
  margin-bottom: 25px;
  margin-top: 5px;
  line-height: 1.5;
}

.auth-form {
  margin-top: 20px;
}

.form-field {
  margin-bottom: 20px;
}

.input-label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  text-align: left;
}

.form-input {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #f2f2f2;
  height: 40px;
}

.password-container {
  position: relative;
}

.eye-icon {
  position: absolute;
  top: 12px;
  right: 15px;
  cursor: pointer;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 20px;
}

.forgot-password {
  font-size: 12px;
  color: #6c63ff;
  text-decoration: none;
}

.auth-button {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 15px;
}

.auth-button:disabled {
  background-color: #a9a5e0;
  cursor: not-allowed;
}

.primary-button {
  background-color: #6c63ff;
  color: white;
}

.primary-button:hover:not(:disabled) {
  background-color: #5a54d1;
}

.secondary-button {
  background-color: white;
  color: #6c63ff;
  border: 1px solid #6c63ff;
}

.secondary-button:hover:not(:disabled) {
  background-color: #f5f5ff;
}

.secondary-button:disabled {
  background-color: white;
  color: #a9a5e0;
  border-color: #a9a5e0;
}

.back-button {
  background-color: transparent;
  color: #999;
  border: none;
  font-size: 12px;
  margin-top: 10px;
  text-decoration: underline;
  cursor: pointer;
}

.back-button:hover {
  color: #555;
}

.divider {
  position: relative;
  height: 1px;
  background-color: #e0e0e0;
  margin: 30px 0;
  text-align: center;
}

.divider-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 0 10px;
  color: #999;
  font-size: 12px;
}

.oauth-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: white;
  color: #333;
  border: 1px solid #ccc;
  font-weight: 500;
}

.oauth-button:hover {
  background-color: #f5f5f5;
  border-color: #999;
}

.oauth-logo {
  width: 20px;
  height: 20px;
}

.microsoft-button {
  margin-bottom: 20px;
}

.signup-text {
  margin-top: 30px;
  font-size: 12px;
  text-align: center;
}

.signup-text a {
  color: #6c63ff;
  text-decoration: none;
}

.signup-text a:hover {
  text-decoration: underline;
}

.copyright {
  font-size: 12px;
  margin-bottom: 40px;
  text-align: center;
}

.error-input {
  border: 1px solid red;
}

.error-message {
  color: red;
  font-size: 0.8em;
  margin-top: 5px;
  text-align: left;
}

.resend-link {
  color: #6c63ff;
  text-decoration: underline;
  cursor: pointer;
  margin-left: 5px;
}

@media (max-width: 768px) {
  .auth-card {
    width: 90%;
    padding: 0 30px;
  }

  .logo-container {
    height: 100px;
  }
}
</style>

<script>
import axios from 'axios';
import { signInWithMicrosoft, resendVerificationEmail, setSessionFromTokens } from '@/supabase/auth';
import { openCalendlyDemo } from '@/utils/calendly';

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

export default {
  data() {
    return {
      step: 1,
      // Étape 1
      email: '',
      password: '',
      showPassword: false,
      loginError: false,
      emailNotVerified: false,
      isLoading: false,
      // Étape 2
      otpCode: '',
      otpError: '',
      isVerifying: false,
      resendCooldown: 0,
      resendTimer: null,
    };
  },

  computed: {
    maskedEmail() {
      if (!this.email) return '';
      const [local, domain] = this.email.split('@');
      if (!domain) return this.email;
      const visible = local.slice(0, 2);
      const masked  = '*'.repeat(Math.max(0, local.length - 2));
      return `${visible}${masked}@${domain}`;
    },
  },

  methods: {
    openCalendlyDemo,
    goBack() {
      this.$router.go(-1);
    },

    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    backToStep1() {
      this.step = 1;
      this.otpCode = '';
      this.otpError = '';
      this.stopResendTimer();
    },

    startResendTimer() {
      this.resendCooldown = 60;
      this.resendTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }, 1000);
    },

    stopResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendCooldown = 0;
    },

    async requestOtp() {
      this.loginError      = false;
      this.emailNotVerified = false;
      this.isLoading       = true;

      try {
        const { data } = await axios.post(`${API_URL}/copilot/request-login-otp`, {
          email:    this.email,
          password: this.password,
        });
        // Pendant une panne du fournisseur d'emails, le serveur renvoie la session
        // directement : le code ne partirait pas et bloquerait tout le monde dehors.
        if (data && data.otp_disabled && data.access_token) {
          await setSessionFromTokens(data.access_token, data.refresh_token);
          // Rechargement complet plutot qu'une navigation interne : le client
          // d'authentification repart du stockage local, exactement comme quand
          // l'utilisateur rafraichit la page lui-meme.
          window.location.assign('/dashboardPage');
          return;
        }
        this.step = 2;
        this.startResendTimer();
      } catch (error) {
        const status = error.response?.status;
        const msg    = error.response?.data?.message || '';

        if (status === 401) {
          if (msg.toLowerCase().includes('vérifié') || msg.toLowerCase().includes('confirmed')) {
            this.emailNotVerified = true;
          } else {
            this.loginError = true;
          }
        } else if (status === 429) {
          alert(this.$t('auth.login.errors.tooManyRequests'));
        } else {
          console.error('[LOGIN] requestOtp error:', error);
          alert(this.$t('auth.login.errors.generic'));
        }
      } finally {
        this.isLoading = false;
      }
    },

    async resendOtp() {
      if (this.resendCooldown > 0) return;
      this.isLoading = true;
      try {
        await axios.post(`${API_URL}/copilot/request-login-otp`, {
          email:    this.email,
          password: this.password,
        });
        this.otpError = '';
        this.startResendTimer();
      } catch (error) {
        console.error('[LOGIN] resendOtp error:', error);
        alert(this.$t('auth.login.errors.resendFailed'));
      } finally {
        this.isLoading = false;
      }
    },

    async verifyOtp() {
      this.otpError    = '';
      this.isVerifying = true;

      try {
        const { data } = await axios.post(`${API_URL}/copilot/verify-login-otp`, {
          email:    this.email,
          otp_code: this.otpCode,
        });
        await setSessionFromTokens(data.access_token, data.refresh_token);
        this.stopResendTimer();
        this.$router.replace({ path: '/dashboardPage' });
      } catch (error) {
        const responseData = error.response?.data || {};
        const code         = responseData.code;

        if (code === 'MAX_ATTEMPTS') {
          this.otpError = this.$t('auth.login.errors.tooManyAttempts');
          setTimeout(() => { this.backToStep1(); }, 2000);
        } else if (code === 'INVALID_CODE') {
          this.otpError = responseData.message || this.$t('auth.login.errors.invalidCode');
          this.otpCode = '';
        } else {
          this.otpError = this.$t('auth.login.errors.expiredCode');
          setTimeout(() => { this.backToStep1(); }, 2000);
        }
      } finally {
        this.isVerifying = false;
      }
    },

    async handleMicrosoftLogin() {
      try {
        await signInWithMicrosoft();
      } catch (error) {
        console.error('[LOGIN] Microsoft SSO error:', error);
        alert(this.$t('auth.login.errors.microsoftError'));
      }
    },

    async handleResendVerification() {
      if (!this.email) {
        alert(this.$t('auth.login.errors.emailRequired'));
        return;
      }
      try {
        this.isLoading = true;
        await resendVerificationEmail(this.email);
        alert(this.$t('auth.login.errors.verificationSent'));
      } catch (error) {
        console.error('Erreur renvoi e-mail de vérification:', error);
        alert(this.$t('auth.login.errors.sendFailed'));
      } finally {
        this.isLoading = false;
      }
    },
  },

  beforeDestroy() {
    this.stopResendTimer();
  },
};
</script>
